import cx from 'classnames'
import { Link, useLocation } from 'react-router-dom'
import type { PlatePage_CulturePlate } from '~/api/backend/pages/platePageAPI'
import type { WellPage_CulturePlate } from '~/api/backend/pages/wellPageAPI'
import cs from './breadcrumbs.scss'
import RightArrowIcon from './icons/RightArrowIcon'

export default function Breadcrumbs({
  plate,
  wellPos,
  className,
}: {
  plate?: PlateProps
  wellPos?: string
  className?: string
}) {
  const location = useLocation()

  const params = new URLSearchParams(location.search)

  // For now, the Cultures breadcrumb links to the Dashboard, not the Cultures page,
  // as we expect the Dashboard to be the most common entry point for the Plate and Well View.
  //
  // Later, we can make the breadcrumbs more dynamic based on the user's navigation history.
  return (
    <nav className={cx(cs.breadcrumbs, className)}>
      <Link key='root' to='/monitor/dashboard'>
        Cultures
      </Link>

      {plate?.id != null ? (
        <>
          <PlateCrumb
            plate={plate}
            // On the well page, the plate crumb links to the currently selected timestamp.
            // On the plate page, the plate crumb is a shortcut to the latest timestamp.
            timestampParam={wellPos ? params.get('t') : null}
            active={wellPos == null}
          />

          {wellPos != null ? (
            <WellCrumb plateID={plate.id} wellPos={wellPos} active />
          ) : null}
        </>
      ) : null}
    </nav>
  )
}

interface PlateProps {
  /** Can be from URL if not yet fetched. */
  id: string
  /** Can be null if not yet fetched. */
  data: PlatePage_CulturePlate | WellPage_CulturePlate | null
}

/**
 * Render a link to the plate, falling back to the cache if the barcode hasn't
 * been fetched yet.
 */
function PlateCrumb({
  plate,
  timestampParam,
  active,
}: {
  plate: PlateProps
  timestampParam: string | null
  active: boolean
}) {
  const { data: queried } = plate

  const barcode = queried?.barcode
  if (!barcode) {
    return null
  }

  const searchParams = timestampParam ? `?t=${timestampParam}` : ''

  return (
    <>
      <RightArrowIcon key='platesep' className={cs.separator} />
      <Link
        key='plate'
        to={`/plate/${plate.id}${searchParams}`}
        className={active ? cs.active : null}
      >
        {barcode}
      </Link>
    </>
  )
}

function WellCrumb({
  plateID,
  wellPos,
  active,
}: {
  plateID: string
  wellPos: string
  active: boolean
}) {
  return (
    <>
      <RightArrowIcon key='wellsep' className={cs.separator} />
      <Link
        key='well'
        to={`/plate/${plateID}/well/${wellPos}`}
        className={active ? cs.active : null}
      >
        {wellPos}
      </Link>
    </>
  )
}
